import { useEffect, useState } from 'preact/hooks';
import { isMonthKey, monthName, monthNameSv, monthOf, nextMonth, prevMonth, fmtMonth, type MonthKey } from '@kff/engine';
import type { Api } from '../api.js';
import { h, weekLabel, weekLabelSv, when, ddm, ddmSv } from '../format.js';
import { texts, useLang } from '../i18n.js';
import { RoleIcon } from '../shell.js';
import { Avatar, Block, Callout, Dialog, LoadError, Loading, MonthStepper, PageHead, Pill, toast } from '../ui.js';

interface Overview {
  month: MonthKey;
  weeks: string[];
  summary: string;
  invitationsOn: boolean;
  banner: string | null;
  waitingFor: { personId: string; name: string }[];
  rows: { personId: string; name: string; role: 'editor' | 'camera'; roleLabel?: string; weeklyBaseH: number; submittedAt: string | null; unit: string | null; weeks: { week: string; hours: number | null; percent: number | null; weekend: { day: string; hours: number }[] }[] }[];
  emails: { label: string; at: string; status: string; statusText: string; recipients: string[] }[];
}

const cap = (x: string) => x.charAt(0).toUpperCase() + x.slice(1);

/** The overview's own texts (English stays the default; Swedish for people who picked it). */
const TX = texts(
  {
    invitationsOff: 'Invitations are off',
    everyoneSubmitted: 'Everyone has submitted',
    title: 'Availability',
    sub: "Freelancers' availability per week – entered by them, used in Capacity",
    month: 'Month',
    prevMonth: 'Previous month',
    nextMonth: 'Next month',
    monthButton: (m: MonthKey) => `${monthName(m)} ${m.slice(0, 4)}`,
    offBody: 'Nobody is emailed – the monthly emails wait as "Not sent – invitations off". An admin turns invitations on in System › Settings › Connections when everything is ready.',
    blockTitle: (m: MonthKey) => `Availability · ${fmtMonth(m)}`,
    blockInfo: 'Freelancers enter percent or hours per week from their personal link. Percent is converted with their weekly base. The Capacity tab counts it as their capacity.',
    remind: 'Remind…',
    remindOff: (why: string) => `Remind… is off: ${why}.`,
    perFreelancer: 'Availability per freelancer',
    freelancer: 'Freelancer',
    status: 'Status',
    week: (w: string) => weekLabel(w).split(' · ')[0],
    role: (r: 'editor' | 'camera'): string => (r === 'camera' ? 'Camera' : 'Editing'),
    submitted: (iso: string) => `Submitted ${when(iso)}`,
    notSubmitted: 'Not submitted',
    weekendDay: (d: string) => ddm(d).slice(0, 3),
    emailsFor: (m: MonthKey) => `Emails for ${monthName(m)}`,
    emailsInfo: 'Sent by the Hub: the invitation on the 20th and a reminder on the 25th to those who have not submitted. Swedish, du-form.',
    to: ' · to ',
    nobody: 'nobody – everyone has submitted',
    noEmails: 'No emails for this month.',
    pickOne: 'Pick at least one',
    queued: (n: number) => `Reminder in the outbox for ${n} freelancer${n === 1 ? '' : 's'}`,
    remindAbout: (m: MonthKey) => `Remind about ${monthName(m)}`,
    notSubmittedN: (n: number) => `${n} freelancer${n === 1 ? ' has' : 's have'} not submitted`,
    preview: 'Preview (Swedish)',
    cancel: 'Cancel',
    send: 'Send reminder'
  },
  {
    invitationsOff: 'Inbjudningar är avstängda',
    everyoneSubmitted: 'Alla har skickat in',
    title: 'Tillgänglighet',
    sub: 'Frilansarnas tillgänglighet per vecka – de fyller i den själva och Kapacitet räknar med den',
    month: 'Månad',
    prevMonth: 'Föregående månad',
    nextMonth: 'Nästa månad',
    monthButton: (m: MonthKey) => `${cap(monthNameSv(m))} ${m.slice(0, 4)}`,
    offBody: 'Ingen får mejl – månadens mejl väntar som ”Inte skickat – inbjudningar avstängda”. En admin slår på inbjudningar i System › Inställningar › Kopplingar när allt är klart.',
    blockTitle: (m: MonthKey) => `Tillgänglighet · ${monthNameSv(m)} ${m.slice(0, 4)}`,
    blockInfo: 'Frilansarna fyller i procent eller timmar per vecka via sin personliga länk. Procent räknas om med deras veckobas. Kapacitet räknar med det.',
    remind: 'Påminn…',
    remindOff: (why: string) => `Påminn… är avstängt: ${why.charAt(0).toLowerCase() + why.slice(1)}.`,
    perFreelancer: 'Tillgänglighet per frilansare',
    freelancer: 'Frilansare',
    status: 'Status',
    week: (w: string) => weekLabelSv(w).split(' · ')[0],
    role: (r: 'editor' | 'camera') => (r === 'camera' ? 'Kamera' : 'Redigering'),
    submitted: (iso: string) => `Inskickad ${when(iso, 'sv')}`,
    notSubmitted: 'Inte inskickad',
    weekendDay: (d: string) => ddmSv(d).slice(0, 3),
    emailsFor: (m: MonthKey) => `Mejl för ${monthNameSv(m)}`,
    emailsInfo: 'Skickas av Hubben: inbjudan den 20:e och en påminnelse den 25:e till dem som inte har skickat in. På svenska, i du-form.',
    to: ' · till ',
    nobody: 'ingen – alla har skickat in',
    noEmails: 'Inga mejl för den här månaden.',
    pickOne: 'Välj minst en',
    queued: (n: number) => `Påminnelse i utkorgen för ${n} frilansare`,
    remindAbout: (m: MonthKey) => `Påminn om ${monthNameSv(m)}`,
    notSubmittedN: (n: number) => `${n} frilansare har inte skickat in`,
    preview: 'Förhandsvisning',
    cancel: 'Avbryt',
    send: 'Skicka påminnelse'
  }
);

const TONE: Record<string, 'ok' | 'warn' | 'info' | 'neutral' | 'crit'> = { sent: 'ok', held: 'warn', queued: 'info', scheduled: 'neutral', failed: 'crit', cancelled: 'neutral' };

/** Producers' overview (UX-1060): "4 of 6 submitted · waiting for …", Remind…, the month's emails. */
export function AvailabilityOverview(p: { api: Api; today: string; canRemind: boolean; month?: string }) {
  const t = TX[useLang()];
  // twelve months back (submissions are kept that long) up to five ahead, like the freelancers' own form
  const now = monthOf(p.today);
  let min = now;
  for (let i = 0; i < 12; i++) min = prevMonth(min);
  let max = now;
  for (let i = 0; i < 5; i++) max = nextMonth(max);
  const [month, setMonth] = useState<MonthKey>(p.month && isMonthKey(p.month) && p.month >= min && p.month <= max ? p.month : nextMonth(now));
  const [o, setO] = useState<Overview | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [remindOpen, setRemindOpen] = useState(false);
  const load = () => p.api.get<Overview>(`/api/availability/overview?month=${month}`).then(setO, setError);
  useEffect(() => {
    void load();
  }, [month]);
  if (error) return <LoadError error={error} />;
  if (!o) return <Loading />;
  const remindReason = !o.invitationsOn ? t.invitationsOff : !o.waitingFor.length ? t.everyoneSubmitted : '';
  return (
    <div class="screen">
      <PageHead title={t.title} sub={t.sub} />
      <div>
        <MonthStepper value={month} min={min} max={max} label={t.monthButton} onChange={setMonth} prevLabel={t.prevMonth} nextLabel={t.nextMonth} groupLabel={t.month} tour="av-months" />
      </div>
      {o.banner ? (
        <Callout tone="warn" title={t.invitationsOff}>
          {t.offBody}
        </Callout>
      ) : null}
      <Block
        title={t.blockTitle(o.month)}
        info={t.blockInfo}
        actions={
          p.canRemind ? (
            <button type="button" class="btn" data-tour="av-remind" disabled={!!remindReason} title={remindReason || undefined} onClick={() => setRemindOpen(true)}>
              {t.remind}
            </button>
          ) : null
        }
      >
        <p class="count">
          <strong>{o.summary.split(' · ')[0]}</strong>
          {o.summary.includes(' · ') ? ` · ${o.summary.split(' · ').slice(1).join(' · ')}` : ''}
        </p>
        {/* with invitations off the banner above already says why; the hint would only repeat it */}
        {remindReason && p.canRemind && (o.invitationsOn || !o.banner) ? <p class="hint">{t.remindOff(remindReason)}</p> : null}
        <div class="tbl-wrap" tabIndex={0} role="region" aria-label={t.perFreelancer} data-tour="av-table">
          <table class="tbl">
            <thead>
              <tr>
                <th scope="col">{t.freelancer}</th>
                <th scope="col">{t.status}</th>
                {o.weeks.map((w) => (
                  <th scope="col" class="num">
                    {t.week(w)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {o.rows.map((r) => (
                <tr>
                  <th scope="row">
                    <span class="who">
                      <Avatar name={r.name} decorative />
                      <span class="who-t">
                        <span class="who-name">
                          {r.name} <RoleIcon role={r.role} label={r.roleLabel ?? t.role(r.role)} />
                        </span>
                        <span class="who-sub">
                          {r.roleLabel ?? t.role(r.role)} · 100 % = {r.weeklyBaseH} h
                        </span>
                      </span>
                    </span>
                  </th>
                  <td>{r.submittedAt ? <Pill tone="ok">{t.submitted(r.submittedAt)}</Pill> : <Pill tone="warn">{t.notSubmitted}</Pill>}</td>
                  {r.weeks.map((w) => (
                    <td class="num">
                      {w.hours === null ? <span class="faint">–</span> : h(w.hours)}
                      {w.percent !== null ? <span class="small muted"> ({w.percent} %)</span> : null}
                      {w.weekend.length ? <span class="cell-sub">+ {w.weekend.map((d) => `${t.weekendDay(d.day)} ${h(d.hours)}`).join(', ')}</span> : null}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Block>
      <Block title={t.emailsFor(o.month)} info={t.emailsInfo}>
        {o.emails.length ? (
          <ul class="list">
            {o.emails.map((e) => (
              <li class="list-row">
                <span>
                  <strong>{e.label}</strong>
                  {t.to}
                  {e.recipients.length ? e.recipients.map((n) => n.split(' ')[0]).join(', ') : t.nobody}
                </span>
                <Pill tone={TONE[e.status] ?? 'neutral'}>{e.statusText}</Pill>
              </li>
            ))}
          </ul>
        ) : (
          <p class="muted small">{t.noEmails}</p>
        )}
      </Block>
      {remindOpen ? <RemindDialog api={p.api} month={o.month} people={o.waitingFor} onClose={() => setRemindOpen(false)} onSent={load} /> : null}
    </div>
  );
}

function RemindDialog(p: { api: Api; month: MonthKey; people: { personId: string; name: string }[]; onClose: () => void; onSent: () => void }) {
  const t = TX[useLang()];
  const [picked, setPicked] = useState<string[]>(p.people.map((x) => x.personId));
  const [preview, setPreview] = useState<{ subject: string; text: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    void p.api.get<{ subject: string; text: string }>(`/api/availability/preview?kind=reminder&month=${p.month}&person=${p.people[0]?.personId ?? ''}`).then(setPreview, () => setPreview(null));
  }, []);
  const send = async () => {
    if (!picked.length) return setError(t.pickOne);
    if (busy) return;
    setBusy(true);
    try {
      const r = await p.api.post<{ queued: { id: string }[] }>('/api/availability/remind', { month: p.month, personIds: picked });
      p.onClose();
      p.onSent();
      toast({
        text: t.queued(r.queued.length),
        undo: async () => {
          for (const q of r.queued) await p.api.post(`/api/availability/outbox/${q.id}/cancel`);
          p.onSent();
        }
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={t.remindAbout(p.month)}
      onClose={p.onClose}
      foot={
        <>
          <button type="button" class="btn" onClick={p.onClose}>
            {t.cancel}
          </button>
          <button type="button" class="btn btn-primary" disabled={busy} onClick={() => void send()}>
            {t.send}
          </button>
        </>
      }
    >
        <p class="sub">
          {t.notSubmittedN(p.people.length)}
        </p>
        <div class="stack">
          {p.people.map((x) => (
            <label class="check">
              <input type="checkbox" checked={picked.includes(x.personId)} onChange={(e) => setPicked((e.target as HTMLInputElement).checked ? [...picked, x.personId] : picked.filter((i) => i !== x.personId))} />
              <Avatar name={x.name} size="sm" decorative />
              {x.name}
            </label>
          ))}
          {preview ? (
            <details>
              <summary>{t.preview}</summary>
              <div class="mail" lang="sv">
                <p>
                  <strong>{preview.subject}</strong>
                </p>
                {preview.text.split('\n\n').map((l) => (
                  <p>{l}</p>
                ))}
              </div>
            </details>
          ) : null}
          {error ? (
            <p class="field-error" role="alert">
              {error}
            </p>
          ) : null}
        </div>
    </Dialog>
  );
}
